import { initResultGallery } from './result-gallery.js';

export function initResults({ scope, gsap, motion }) {
  const win = scope.ownerDocument.defaultView;

  scope.document.querySelectorAll('[data-results]').forEach((root) => {
    initResultGallery(root, { gsap, motion, scope });
    const floating = root.querySelector('.aw-results__floating');
    const rows = [...root.querySelectorAll('[data-result]')];
    const fine = win.matchMedia('(hover: hover) and (pointer: fine) and (min-width: 768px)');
    const decoded = new Map();
    let active = null;
    let previousX = 0;
    let serial = 0;
    const xTo = floating ? gsap.quickTo(floating, 'x', { duration: .45, ease: 'power3.out' }) : null;
    const yTo = floating ? gsap.quickTo(floating, 'y', { duration: .45, ease: 'power3.out' }) : null;
    const rotateTo = floating ? gsap.quickTo(floating, 'rotation', { duration: .45, ease: 'power3.out' }) : null;

    const hide = () => {
      serial++;
      active?.classList.remove('is-active');
      active = null;
      if (floating) floating.style.visibility = 'hidden';
      [xTo, yTo, rotateTo].forEach((move) => move?.tween.pause?.());
    };

    const load = (row) => {
      if (!row.dataset.photo) return Promise.resolve(null);
      if (!decoded.has(row)) {
        const photo = new win.Image();
        photo.alt = '';
        photo.decoding = 'async';
        photo.sizes = '300px';
        if (row.dataset.photoSrcset) photo.srcset = row.dataset.photoSrcset;
        photo.src = row.dataset.photo;
        decoded.set(row, photo.decode().then(() => photo.naturalWidth && photo.naturalHeight ? photo : null).catch(() => null));
      }
      return decoded.get(row);
    };

    const position = (point, animate) => {
      const x = Math.max(16, Math.min(win.innerWidth - floating.offsetWidth - 16, point.clientX + 24));
      const y = Math.max(16, Math.min(win.innerHeight - floating.offsetHeight - 16, point.clientY - 60));
      if (animate) {
        xTo(x);
        yTo(y);
        rotateTo(gsap.utils.clamp(-8, 8, (point.clientX - previousX) * .14));
      } else {
        gsap.set(floating, { x, y, rotation: 0 });
      }
      previousX = point.clientX;
    };

    const follow = (event) => {
      if (active && fine.matches && motion.enabled && event.pointerType !== 'touch' && floating) position(event, true);
    };

    rows.forEach((row) => {
      const reveal = async (event) => {
        hide();
        if (!row.dataset.photo || !fine.matches || scope.editor || event?.pointerType === 'touch' || !floating) return;
        active = row;
        const request = serial;
        const photo = await load(row);
        if (!scope.alive || !photo || active !== row || request !== serial) return;

        // Mount the row's decoded image, never resize the previous row's bitmap.
        // Each portrait/landscape carries its own intrinsic dimensions and srcset.
        photo.width = photo.naturalWidth;
        photo.height = photo.naturalHeight;
        floating.style.setProperty('--aw-result-ratio', `${photo.naturalWidth} / ${photo.naturalHeight}`);
        floating.replaceChildren(photo);
        row.classList.add('is-active');
        const bounds = row.getBoundingClientRect();
        const point = event && typeof event.clientX === 'number'
          ? event : { clientX: bounds.right - 360, clientY: bounds.bottom };
        position(point, false);
        floating.style.visibility = 'visible';
      };

      const leave = () => { if (active === row) hide(); };
      scope.on(row, 'pointerenter', reveal);
      scope.on(row, 'pointermove', follow, { passive: true });
      scope.on(row, 'pointerleave', leave);
      scope.on(row, 'pointercancel', leave);
      // Keyboard access uses the same preview; a mouse click has no action.
      scope.on(row, 'focus', (event) => { if (row.matches(':focus-visible')) reveal(event); });
      scope.on(row, 'blur', leave);
      scope.on(row, 'keydown', (event) => { if (event.key === 'Escape') hide(); });
    });

    const reset = () => {
      hide();
      rows.forEach((row) => {
        if (fine.matches && row.dataset.photo && !scope.editor) row.tabIndex = 0;
        else row.removeAttribute('tabindex');
      });
    };
    const preloader = new scope.IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        rows.forEach(load);
        preloader.disconnect();
      }
    }, { rootMargin: '1000px' });
    preloader.observe(root);
    scope.on(scope.ownerDocument, 'scroll', hide, { passive: true, capture: true });
    scope.on(win, 'blur', hide);
    scope.on(win, 'resize', hide);
    scope.on(fine, 'change', reset);
    scope.on(scope.ownerDocument, 'aw:motionchange', reset);
    scope.onDispose(() => { hide(); floating?.replaceChildren(); decoded.clear(); });
    reset();
  });
}
